import { useCallback, useEffect, useState } from "react";
import { BookOpen, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { useAuthImage, useJob } from "../../hooks/useJob";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { OutputsList } from "../OutputsList";

function Flip({ pages, rtl }) {
  const [i, setI] = useState(0);
  const p = pages[i];
  const src = useAuthImage(p ? `/comic-pages/${p.id}/render?w=900` : null, p?.updated_at);
  if (!pages.length) return null;
  const next = () => setI(Math.min(pages.length - 1, i + 1));
  const prev = () => setI(Math.max(0, i - 1));
  return (
    <div className="panel p-4" data-testid="comic-reader-preview">
      <p className="eyebrow mb-3 flex items-center gap-1"><BookOpen size={12} />Reader preview · {rtl ? "right to left" : "left to right"}</p>
      <div className="flex items-center gap-3">
        <button onClick={rtl ? next : prev} className="text-slate-400 hover:text-white" data-testid="reader-left"><ChevronLeft /></button>
        <div className="flex-1 max-w-md mx-auto">{src ? <img src={src} alt={`Page ${i + 1}`} className="w-full rounded shadow-lg" /> : <div className="aspect-[2/3] bg-slate-900 rounded animate-pulse" />}</div>
        <button onClick={rtl ? prev : next} className="text-slate-400 hover:text-white" data-testid="reader-right"><ChevronRight /></button>
      </div>
      <p className="text-center text-xs text-slate-500 mt-2" data-testid="reader-page-count">Page {i + 1} of {pages.length}</p>
    </div>
  );
}

export default function ComicEbookTab({ book }) {
  const [outs, setOuts] = useState([]);
  const [pages, setPages] = useState(null);
  const [settings, setSettings] = useState(null);
  const [concepts, setConcepts] = useState([]);
  const [cover, setCover] = useState("");
  const [isbn, setIsbn] = useState("");
  const load = useCallback(() => api.get(`/books/${book.id}/ebook`).then((r) => setOuts(r.data)), [book.id]);
  useEffect(() => {
    load();
    api.get(`/books/${book.id}/comic/pages`).then((r) => setPages(r.data));
    api.get(`/books/${book.id}/comic`).then((r) => setSettings(r.data.settings));
    api.get(`/books/${book.id}/cover-concepts`).then((r) => setConcepts(r.data)).catch(() => {});
  }, [book.id, load]);
  const [job, start, busy] = useJob(load, "Fixed-layout ebook ready");
  const build = async () => { try { const { data } = await api.post(`/books/${book.id}/comic/ebook`, { cover_concept_id: cover || null, isbn }); start(data.id); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="grid xl:grid-cols-2 gap-6" data-testid="comic-ebook-tab">
      <div className="space-y-4">
        <div className="panel p-5 space-y-3">
          <p className="eyebrow">Kindle fixed-layout ebook</p>
          <p className="text-xs text-slate-400">Every page becomes one full-screen image, the way Kindle comics and manga are sold. {settings?.direction === "rtl" ? "Pages turn right-to-left (manga) on Kindle." : "Pages turn left-to-right."} Upload the .epub file on KDP as your Kindle eBook manuscript.</p>
          <div className="grid sm:grid-cols-2 gap-2">
            <select value={cover} onChange={(e) => setCover(e.target.value)} className="h-9 rounded-md bg-slate-900 border border-slate-700 px-2 text-sm" data-testid="comic-ebook-cover"><option value="">Cover: use page 1</option>{concepts.map((c) => <option key={c.id} value={c.id}>Cover concept · {c.edition}</option>)}</select>
            <Input value={isbn} onChange={(e) => setIsbn(e.target.value)} placeholder="ISBN (optional)" className="h-9" data-testid="comic-ebook-isbn" />
          </div>
          <Button onClick={build} disabled={busy || !pages?.length} className="bg-amber-600 hover:bg-amber-500" data-testid="comic-ebook-build">{busy ? `Building… ${job?.progress || 0}%` : pages ? `Build ebook (${pages.length} page${pages.length === 1 ? "" : "s"})` : "Loading pages…"}</Button>
          {settings?.color === "bw" && <p className="text-[11px] text-slate-500">Black & white is on — every page, including your uploaded art, is rendered in greyscale.</p>}
        </div>
        <OutputsList items={outs} testid="comic-ebook-outputs" />
      </div>
      <Flip pages={pages || []} rtl={settings?.direction === "rtl"} />
    </div>
  );
}
